import React, {
  useEffect,
  useState,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  useGetMediaByFaxId,
  useResendFax,
} from 'pages/Dashboard/pages/Fax/hooks';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  useMedia,
  useNotify,
} from 'core/hooks';
import IconButton from '@mui/material/IconButton';
import ToolTip from 'core/components/Tooltip';
import {
  Entity,
} from 'pages/Dashboard/contexts/sidePanel';
import Button from 'pages/Dashboard/components/Button';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import isNil from 'lodash/isNil';
import {
  formatContact,
  isNilOrZero,
} from '@populateui/utils';
import Name from 'pages/Dashboard/components/Name';
import DetailedItem from 'core/components/DetailedItem';
import AddPatientDialog, {
  AddPatientDialogParams,
} from 'pages/Dashboard/pages/Fax/Overview/AddPatientDialog';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import ClockIcon from 'core/icons/clock';
import {
  faxEventChannel,
  toDosEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import clsx from 'clsx';
import FaxSelect from 'pages/Dashboard/pages/Fax/FaxSelect';
import FaxToDos from 'pages/Dashboard/pages/Fax/Overview/FaxToDos';
import {
  compactPatient,
} from 'pages/Dashboard/utils/helper';
import Editable from 'core/components/Editable';

type FaxDetailsProps = {
  entity: Entity;
  onClose?: () => void;
};

const detailedItemClasses: Readonly<Record<string, string>> = Object.freeze({
  root: '!p-0',
});

export default function FaxDetails({ entity, onClose }: FaxDetailsProps) {
  // todo: find out if this is needed
  const [addPatientParams, setAddPatientParams] = useState<AddPatientDialogParams | null>(null);
  const {
    date,
    from,
    to,
    message,
    id: _id,
    category,
    errorMessage,
    patient,
    refetchFaxes,
    state,
    subject,
    direction,
    status,
  } = entity;
  const isProcessing = state === 'processing';
  const id = _id as number;
  const hasPatient = !isNil(patient);
  const { data } = useGetMediaByFaxId(id, !isProcessing);
  const notify = useNotify();
  const { getMediaPathById } = useMedia();
  const {
    mutateAsync: resendFax,
    isPending: isLoadingResendFax,
    isError: isErrorResendFax,
    isSuccess: isSuccessResendFax,
  } = useResendFax();

  useEffect(() => {
    if (isErrorResendFax) {
      notify({
        variant: 'error',
        message: 'Fax Send Failed',
      });
    }
  }, [isErrorResendFax]);

  useEffect(() => {
    if (isSuccessResendFax) {
      notify({
        variant: 'success',
        message: 'Fax Sent Successfully',
      });
      faxEventChannel.emit('onFaxChange');
    }
  }, [isSuccessResendFax]);

  useEffect(() => {
    if (isProcessing) {
      refetchFaxes?.();
    }
  }, [isProcessing]);

  const handleResend = async () => {
    await resendFax({ outboundFaxId: id });
  };

  const missingMultiMediaMessage = isProcessing
    ? 'Fax sending in progress, multimedia will be available within a few minutes'
    : 'Nothing to see here...';

  return (
    <div className="flex flex-col flex-1 h-full gap-4">
      <div className="w-full px-4 pb-4 pt-2 border-b border-gray-200 flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <div className="flex gap-3 font-semibold text-lg my-2">
            Fax
            <div className="flex items-center text-xs font-normal gap-3">
              <FaxSelect
                id={id ?? 0}
                status={status}
                category={category}
                errorMessage={errorMessage}
                onChange={() => { faxEventChannel.emit('onUpdateFaxDetails', id); }}
                direction={direction}
              />
              <div className="flex flex-row items-center cursor-pointer">
                <ToolTip title="Sent on">
                  <span>
                    <ClockIcon className="!w-4 !h-4 !text-gray-500" />
                  </span>
                </ToolTip>
                <p className="nw-appointment-time-range ml-1 w-40">
                  {date}
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-1 items-center">
            {category === 'Send Failed' && (
              <ToolTip title="Resend">
                <span>
                  <IconButton
                    color="info"
                    onClick={handleResend}
                    disabled={isLoadingResendFax}
                  >
                    <RefreshIcon
                      className={clsx(
                        '!text-xl',
                        { '!text-blue-500': !isLoadingResendFax },
                      )}
                    />
                  </IconButton>
                </span>
              </ToolTip>
            )}
            <ToolTip title="Close">
              <span>
                <IconButton className="!-mr-2" onClick={onClose}>
                  <CloseIcon className="!text-xl" />
                </IconButton>
              </span>
            </ToolTip>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-xs flex gap-4 justify-evenly w-full">
            {[[from, 'From'], [to, 'To']].map(([contactString, label]) => {
              const [name, phone] = formatContact(contactString ?? '');
              return (
                <DetailedItem
                  key={label}
                  label={label}
                  classes={detailedItemClasses}
                >
                  <Name
                    name={name}
                    image={{ alt: name?.split(', ')?.reverse()?.join(' ') ?? '' }}
                  >
                    {phone}
                  </Name>
                </DetailedItem>
              );
            })}
            <DetailedItem label="Patient" classes={detailedItemClasses}>
              <Editable
                idleView={(
                  <Name
                    link={hasPatient ? getPatientChartPath(patient?.patientId) : undefined}
                    {...patient?.formattedNames}
                    placeholder="Not Assigned"
                    {...((patient?.patientId ?? 0) > 0 && {
                      image: { src: getMediaPathById(patient?.patientId) ?? undefined },
                    })}
                    labelClassName={clsx({ '!text-gray-400': isNilOrZero(patient?.patientId) })}
                  />
                )}
                onShowControl={(e) => {
                  setAddPatientParams({ id });
                }}
                readonly={direction === 'Outbound'}
                tooltip="Change Patient"
                classes={{ idleRoot: '!p-0' }}
              >
                {(onSubmit, onCancel, ref) => (
                  <>
                    <Name
                      link={hasPatient ? getPatientChartPath(patient?.patientId) : undefined}
                      {...patient?.formattedNames}
                      placeholder="Not Assigned"
                      {...(!isNilOrZero(patient?.patientId) && {
                        image: { src: getMediaPathById(patient?.patientId) ?? undefined },
                      })}
                      labelClassName={clsx({ '!text-gray-400': isNilOrZero(patient?.patientId) })}
                    />
                    {!isNil(addPatientParams) && (
                      <AddPatientDialog
                        {...addPatientParams}
                        open
                        onClose={() => {
                          onCancel();
                          setAddPatientParams(null);
                        }}
                      />
                    )}
                  </>
                )}
              </Editable>
            </DetailedItem>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1 px-4">
        <span className="font-semibold text-xs">Subject</span>
        <span className="text-xs">{subject ?? 'No subject available'}</span>
      </div>
      <div className="flex flex-col gap-1 px-4">
        <span className="font-semibold text-xs">Message</span>
        <span className="text-xs">{message ?? 'No message available'}</span>
      </div>
      <div className="flex flex-col gap-1 px-4">
        <span className="font-semibold text-xs">To-dos</span>
        <div className="flex items-center gap-1 flex-wrap">
          <ToolTip
            title={hasPatient ? 'Create To-do' : 'Add Patient to create To-do'}
          >
            <div className={clsx({ 'cursor-not-allowed': !hasPatient })}>
              <Button
                onClick={() => {
                  toDosEventChannel.emit('onCreateToDo', {
                    patient: compactPatient(patient ?? {}),
                    faxId: id,
                  });
                }}
                variant="outlined"
                disabled={!hasPatient}
                color={hasPatient ? 'primary' : undefined}
                className={clsx(
                  '!max-w-[initial] !min-w-[initial] !py-0 cursor-pointer !px-2 !rounded !border !border-dashed !w-20 items-center justify-center flex',
                  { '!border-blue-500': hasPatient },
                )}
              >
                <AddIcon className="!w-4 !h-4 !text-inherit" />
              </Button>
            </div>
          </ToolTip>
          <FaxToDos faxId={id} />
        </div>
      </div>
      <div className="flex flex-col gap-1 px-4 h-full">
        <span className="font-semibold text-xs">Attachments</span>
        {data?.list?.length
          ? data.list.map((media: MultiMediaResponse) => (
            <MediaPreview
              key={media.mediaId}
              thumbnail
              media={media}
              className="h-full"
              hasDelete={false}
            />
          ))
          : <span className="text-xs">{missingMultiMediaMessage}</span>}
      </div>
    </div>
  );
}
